/**
 * React Error Boundary
 *
 * Catches unhandled JavaScript errors in any child component tree and
 * renders a user-friendly fallback UI instead of a blank white screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <MyComponent />
 *   </ErrorBoundary>
 *
 *   // With a custom fallback:
 *   <ErrorBoundary fallback={<p>Something went wrong.</p>}>
 *     <MyComponent />
 *   </ErrorBoundary>
 */

import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional custom fallback element. Overrides the default card UI. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console in development
    console.error("[ErrorBoundary] Caught error:", error, info.componentStack);

    // In production, report to Sentry if configured
    if (typeof window !== "undefined" && (window as any).__sentry_init__) {
      try {
        const Sentry = (window as any).Sentry;
        if (Sentry?.captureException) {
          Sentry.captureException(error, {
            extra: { componentStack: info.componentStack },
          });
        }
      } catch {
        // Sentry not available — silently ignore
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-7 w-7 text-destructive" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-bold">Something went wrong</h2>
                <p className="text-sm text-muted-foreground">
                  An unexpected error occurred. The team has been notified.
                </p>
              </div>
              {this.state.error && (
                <details className="w-full rounded-md border bg-muted px-3 py-2 text-left text-xs text-muted-foreground">
                  <summary className="cursor-pointer font-medium">Error details</summary>
                  <p className="mt-1 break-words font-mono">
                    {this.state.error.message}
                  </p>
                </details>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={this.handleReset}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try again
                </Button>
                <Button onClick={() => window.location.assign("/dashboard")}>
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Lightweight page-level error boundary that renders a compact card.
 * Use this to wrap individual page sections so one section failing
 * doesn't crash the entire page.
 */
export class SectionErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error("[SectionErrorBoundary]", error);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="flex items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-center">
          <div className="space-y-2">
            <AlertTriangle className="mx-auto h-5 w-5 text-destructive" />
            <p className="text-sm font-medium text-destructive">
              This section failed to load
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Retry
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
